import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Calendar,
  Settings,
  Mic,
  Bell,
  Shield,
  ToggleLeft,
  ToggleRight,
  X,
  Crown,
  Lock,
  CheckCircle2,
  Loader2,
  Eye,
  EyeOff,
  Key,
  Edit3,
  Save
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsTab({
  client,
  twilioSettings,
  savingSettings,
  updateTwilioSetting,
  calendarStatus,
  connectingCalendar,
  handleConnectCalendar,
  handleDisconnectCalendar,
  editingAccount,
  accountForm,
  setAccountForm,
  startEditingAccount,
  cancelEditingAccount,
  saveAccountInfo,
  savingAccount,
  privacyNoticeDismissed,
  setPrivacyNoticeDismissed,
  hasFeatureAccess,
  setShowUpgradeModal
}) {
  // Password management state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Phone number editing state
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/client/set-password`, {
        password: newPassword
      }, { withCredentials: true });

      if (response.data.ok) {
        toast.success('Password updated successfully');
        setShowPasswordForm(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        toast.error(response.data.message || 'Failed to update password');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  const startEditingPhone = () => {
    setPhoneNumber(twilioSettings?.twilio_phone || '');
    setEditingPhone(true);
  };

  const cancelEditingPhone = () => {
    setEditingPhone(false);
    setPhoneNumber('');
  };

  const savePhoneNumber = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setSavingPhone(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/webhook/twilio/customer/${client?.customer_id}/phone`,
        { phone_number: phoneNumber },
        { withCredentials: true }
      );

      if (response.data.success || response.data.ok) {
        toast.success('AI phone number updated');
        setEditingPhone(false);
        // Trigger a refresh of settings
        if (typeof updateTwilioSetting === 'function') {
          // Force refresh by toggling a setting back to itself
          window.location.reload();
        }
      } else {
        toast.error(response.data.message || 'Failed to update phone number');
      }
    } catch (error) {
      console.error('Error updating phone:', error);
      toast.error(error.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setSavingPhone(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-slate-800">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-sky-400" />
            AI Phone Settings
          </h2>
          <p className="text-slate-400 text-sm mt-1">Configure your AI receptionist preferences</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Phone Number Status */}
          <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                twilioSettings?.phone_service_active ? 'bg-green-500/20' : 'bg-slate-500/20'
              }`}>
                <Phone className={`w-5 h-5 ${
                  twilioSettings?.phone_service_active ? 'text-green-400' : 'text-slate-400'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Your AI Phone Number</h3>
                {editingPhone ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="bg-slate-800 border-slate-700 text-white h-8 w-48"
                      data-testid="phone-number-input"
                    />
                    <Button
                      size="sm"
                      onClick={savePhoneNumber}
                      disabled={savingPhone}
                      className="bg-green-500 hover:bg-green-600 h-8 px-2"
                      data-testid="save-phone-btn"
                    >
                      {savingPhone ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={cancelEditingPhone}
                      className="h-8 px-2 text-slate-400"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">
                    {twilioSettings?.twilio_phone || 'Not assigned yet'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!editingPhone && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={startEditingPhone}
                  className="text-slate-400 hover:text-white"
                  data-testid="edit-phone-btn"
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
              )}
              {twilioSettings?.phone_service_active ? (
                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                  Active
                </span>
              ) : (
                <span className="px-3 py-1 bg-slate-500/20 text-slate-400 text-sm rounded-full">
                  Inactive
                </span>
              )}
            </div>
          </div>

          {/* Call Recording Setting */}
          <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Mic className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-medium">Call Recording</h3>
                <p className="text-sm text-slate-400">
                  Record incoming calls for quality assurance
                </p>
              </div>
            </div>
            <button
              onClick={() => updateTwilioSetting('call_recording_enabled', !twilioSettings?.call_recording_enabled)}
              disabled={savingSettings}
              className="relative"
            >
              {twilioSettings?.call_recording_enabled ? (
                <ToggleRight className="w-12 h-8 text-green-400" />
              ) : (
                <ToggleLeft className="w-12 h-8 text-slate-500" />
              )}
            </button>
          </div>

          {/* SMS Notifications Setting */}
          <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium">SMS Notifications</h3>
                <p className="text-sm text-slate-400">
                  Receive text notifications for new calls and messages
                </p>
              </div>
            </div>
            <button
              onClick={() => updateTwilioSetting('sms_notifications_enabled', !twilioSettings?.sms_notifications_enabled)}
              disabled={savingSettings}
              className="relative"
            >
              {twilioSettings?.sms_notifications_enabled ? (
                <ToggleRight className="w-12 h-8 text-green-400" />
              ) : (
                <ToggleLeft className="w-12 h-8 text-slate-500" />
              )}
            </button>
          </div>

          {/* Privacy Notice - Dismissible */}
          {!privacyNoticeDismissed && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg relative">
              <button
                onClick={() => {
                  setPrivacyNoticeDismissed(true);
                  localStorage.setItem('privacyNoticeDismissed', 'true');
                }}
                className="absolute top-2 right-2 p-1 hover:bg-amber-500/20 rounded-full transition-colors"
                aria-label="Dismiss privacy notice"
                data-testid="dismiss-privacy-notice"
              >
                <X className="w-4 h-4 text-amber-400" />
              </button>
              <div className="flex items-start gap-3 pr-6">
                <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-400">Privacy Notice</h3>
                  <p className="text-sm text-slate-300 mt-1">
                    Call recordings are stored securely and retained for 30 days. 
                    Callers are informed when recording is enabled. 
                    You can request deletion of recordings at any time.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Account Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Account Information</h2>
          {!editingAccount ? (
            <Button
              variant="outline"
              size="sm"
              onClick={startEditingAccount}
              className="border-slate-700 text-slate-300"
              data-testid="edit-account-btn"
            >
              <Settings className="w-4 h-4 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={cancelEditingAccount}
                className="border-slate-700 text-slate-300"
                data-testid="cancel-edit-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveAccountInfo}
                disabled={savingAccount}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="save-account-btn"
              >
                {savingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          )}
        </div>
        
        <div className="grid gap-4">
          <div className="flex justify-between items-center py-2 border-b border-slate-800">
            <span className="text-slate-400">Email</span>
            {editingAccount ? (
              <input
                type="email"
                value={accountForm.email}
                onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm w-64"
                data-testid="email-input"
              />
            ) : (
              <span className="font-medium">{client?.email}</span>
            )}
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-800">
            <span className="text-slate-400">Business Name</span>
            {editingAccount ? (
              <input
                type="text"
                value={accountForm.business_name}
                onChange={(e) => setAccountForm({...accountForm, business_name: e.target.value})}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm w-64"
                data-testid="business-name-input"
              />
            ) : (
              <span className="font-medium">{client?.business_name || 'Not set'}</span>
            )}
          </div>
          <div className="flex justify-between items-center py-2 border-b border-slate-800">
            <span className="text-slate-400">Customer ID</span>
            <span className="font-mono text-sm text-slate-500">{client?.customer_id || 'N/A'}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-slate-400">Account Status</span>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
              Active
            </span>
          </div>
        </div>
      </motion.div>

      {/* Password Management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Password & Security
            </h2>
            <p className="text-slate-400 text-sm mt-1">Manage your login credentials</p>
          </div>
          {!showPasswordForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPasswordForm(true)}
              className="border-slate-700 text-slate-300"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-1" /> Change Password
            </Button>
          )}
        </div>

        {showPasswordForm ? (
          <div className="space-y-4 p-4 bg-slate-800/30 rounded-lg">
            <div>
              <label className="block text-sm text-slate-400 mb-2">New Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 chars)"
                  className="bg-slate-800 border-slate-700 text-white pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Confirm New Password</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPasswordForm(false);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleChangePassword}
                disabled={savingPassword || !newPassword || !confirmPassword}
                className="bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="save-password-btn"
              >
                {savingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-slate-800/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <p className="text-slate-300">Password is set</p>
                <p className="text-sm text-slate-500">Click "Change Password" to update</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Google Calendar Integration - Premium Only */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={`bg-slate-900/50 border rounded-xl overflow-hidden ${hasFeatureAccess('google_calendar') ? 'border-slate-800' : 'border-slate-800/50'}`}
      >
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-400" />
              Google Calendar Integration
              {!hasFeatureAccess('google_calendar') && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              Connect your calendar to enable AI appointment booking
            </p>
          </div>
        </div>
        
        {hasFeatureAccess('google_calendar') ? (
          <div className="p-6">
            {calendarStatus?.connected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-emerald-400">Calendar Connected</h3>
                      <p className="text-sm text-slate-400">{calendarStatus.email}</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleDisconnectCalendar}
                    variant="outline"
                    size="sm"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="disconnect-calendar-btn"
                  >
                    Disconnect
                  </Button>
                </div>
                
                <div className="p-4 bg-slate-800/30 rounded-lg">
                  <h3 className="font-medium mb-2">What this enables:</h3>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      AI can book appointments directly on your calendar
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Customers see your real availability
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      Automatic appointment confirmations
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-medium">Calendar Not Connected</h3>
                      <p className="text-sm text-slate-400">
                        Connect your Google Calendar for AI scheduling
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleConnectCalendar}
                    disabled={connectingCalendar}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    data-testid="connect-calendar-btn"
                  >
                    {connectingCalendar ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Calendar className="w-4 h-4 mr-2" />
                    )}
                    Connect Calendar
                  </Button>
                </div>
              
              <div className="p-4 bg-sky-500/10 border border-sky-500/30 rounded-lg">
                <h3 className="font-medium text-sky-400 mb-2">Why connect your calendar?</h3>
                <ul className="text-sm text-slate-300 space-y-2">
                  <li>• Your AI can book appointments when customers call or text</li>
                  <li>• Avoid double-bookings with real-time availability</li>
                  <li>• Both you and the customer get calendar confirmations</li>
                </ul>
              </div>
            </div>
          )}
          </div>
        ) : (
          /* Premium Feature Locked */
          <div className="p-6">
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-yellow-400" />
              </div>
              <h3 className="font-bold text-lg mb-2">Premium Feature</h3>
              <p className="text-slate-400 text-sm mb-4 max-w-sm mx-auto">
                Google Calendar integration is available on the Premium plan. Sync your calendar for seamless AI-powered scheduling.
              </p>
              <Button
                onClick={() => setShowUpgradeModal(true)}
                className="bg-gradient-to-r from-yellow-500 to-amber-500 text-black hover:from-yellow-400 hover:to-amber-400"
                data-testid="upgrade-google-cal-btn"
              >
                <Crown className="w-4 h-4 mr-2" /> Upgrade to Premium
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
